import { defaultSchema } from 'hast-util-sanitize';

/**
 * Note-insert policy for every custom element a chat answer may contain (the
 * Streamdown sanitize allowlist in `streamdown-config.ts`). `unwrap` keeps
 * the wrapped markdown; `remove` drops the element and its content.
 *
 * The drift guard in the test file fails when this registry and the sanitize
 * schema disagree, so a tag cannot become renderable in chat without
 * declaring how note inserts flatten it.
 */
export const CUSTOM_CHAT_TAG_POLICY = {
  keypoints: 'unwrap',
  keysafetynotes: 'unwrap',
  safetycallout: 'unwrap',
  usernotes: 'unwrap',
  project: 'unwrap',
  chat: 'unwrap',
  ref: 'unwrap',
  'cite-badge': 'remove',
  'source-cite': 'remove',
  'evidence-image': 'remove',
  'heidi-button': 'remove',
  'img-figure': 'remove',
} as const satisfies Record<string, 'unwrap' | 'remove'>;

const REMOVED_TAGS = Object.entries(CUSTOM_CHAT_TAG_POLICY)
  .filter(([, policy]) => policy === 'remove')
  .map(([tag]) => tag);

// Standard html passes through untouched — marked and the editors already
// handle it. Delegated to the same allowlist the chat sanitizer uses.
const STANDARD_HTML_TAGS = new Set(defaultSchema.tagNames);

const isStandardHtmlTag = (tag: string): boolean =>
  STANDARD_HTML_TAGS.has(tag.toLowerCase());

/**
 * Convert a chat answer's custom Streamdown tags to plain markdown safe for
 * note insertion.
 *
 * Left in place, CommonMark treats a custom opening tag as an html block
 * running to the next blank line, so the markdown-native editor swallows the
 * wrapped bullets as raw text (one collapsed paragraph of literal `- **…**`).
 *
 * `remove`-policy elements go entirely; every other non-standard element
 * unwraps — a wrapper tag alone on its line becomes a blank line (the inner
 * markdown lexes as its own blocks) and an inline tag is dropped in place.
 * Unwrapping is also the fallback for tags this module has never seen, so an
 * unregistered tag degrades to "content kept, tag gone", never a swallowed
 * paragraph. Tag names match case-insensitively (models emit
 * `<keySafetyNotes>`).
 */
export function stripCustomChatTags(markdown: string): string {
  if (!markdown) return markdown;

  let result = markdown;
  for (const tag of REMOVED_TAGS) {
    // Arms in order: self-closing form, a paired element whose content holds
    // no other opener of the same tag, then a lone opening tag — so an
    // unclosed or self-closing marker can never pair with a later `</tag>`
    // and delete the prose in between. `(?=[\s/>])` blocks prefix matches on
    // longer tag names. Stray closers fall through to the unwrap passes.
    const open = `<${tag}(?=[\\s/>])`;
    result = result.replace(
      new RegExp(
        `${open}[^>]*/>|${open}[^>]*>(?:(?!<${tag}[\\s/>])[\\s\\S])*?</${tag}>|${open}[^>]*>`,
        'gi'
      ),
      ''
    );
  }

  // Wrapper tag alone on its line → blank line (the line's newlines remain).
  result = result.replace(
    /^[ \t]*<\/?([a-zA-Z][\w-]*)(?:\s[^>]*)?\/?>[ \t]*$/gm,
    (match, tag: string) => (isStandardHtmlTag(tag) ? match : '')
  );
  // Remaining inline non-standard tags → dropped in place, sentence kept.
  result = result.replace(/<\/?([a-zA-Z][\w-]*)(?:\s[^>]*)?\/?>/g, (match, tag: string) =>
    isStandardHtmlTag(tag) ? match : ''
  );

  return result.replace(/\n{3,}/g, '\n\n').trim();
}
